import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { analyticsService } from '@/services/api'
import { Search, GraduationCap, UserCog, Clock, CheckCircle2, UserX } from 'lucide-react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'

const STATUS_LABELS = {
  on_time:    { label: 'Вовремя',     cls: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  late:       { label: 'Опоздал',     cls: 'bg-amber-50 text-amber-600 border-amber-100' },
  very_late:  { label: 'Сильно',      cls: 'bg-rose-50 text-rose-600 border-rose-100' },
  no_schedule:{ label: 'Без расп.',   cls: 'bg-slate-50 text-slate-500 border-slate-200' },
  absent:     { label: 'Не явился',   cls: 'bg-red-50 text-red-500 border-red-100' },
}

function PersonRow({ person }) {
  const isEmployee = person.person_type === 'employee'
  const statusCfg = STATUS_LABELS[person.attendance_status] || STATUS_LABELS.no_schedule

  return (
    <div className="flex items-center gap-3 py-2.5 px-4 hover:bg-slate-50 rounded-xl transition-colors">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border text-[10px] font-black ${
        isEmployee ? 'bg-violet-50 text-violet-600 border-violet-100' : 'bg-blue-50 text-blue-600 border-blue-100'
      }`}>
        {person.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-800 truncate">{person.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border flex items-center gap-1 ${
            isEmployee ? 'bg-violet-50 text-violet-600 border-violet-100' : 'bg-blue-50 text-blue-600 border-blue-100'
          }`}>
            {isEmployee ? <UserCog size={9} /> : <GraduationCap size={9} />}
            {isEmployee ? 'Сотрудник' : 'Студент'}
          </span>
          {person.time && (
            <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
              <Clock size={10} />
              {format(new Date(person.time), 'HH:mm', { locale: ru })}
            </span>
          )}
        </div>
      </div>
      <div className="shrink-0">
        <span className={`text-[9px] font-black uppercase tracking-wide px-2 py-1 rounded-lg border ${statusCfg.cls}`}>
          {statusCfg.label}
          {person.late_minutes > 0 ? ` +${person.late_minutes}м` : ''}
        </span>
      </div>
    </div>
  )
}

export default function StatPersonList({ stat, title }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const handleSearch = useCallback((e) => {
    const val = e.target.value
    setSearch(val)
    setPage(1)
    clearTimeout(window._statSearchTimer)
    window._statSearchTimer = setTimeout(() => setDebouncedSearch(val), 300)
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stat', stat, debouncedSearch, page],
    queryFn: () => analyticsService.dashboardStatDetail({ stat, search: debouncedSearch, page }).then(r => r.data),
    keepPreviousData: true,
    enabled: !!stat,
  })

  const total = data?.total || 0
  const results = data?.results || []
  const pageSize = data?.page_size || 25
  const totalPages = Math.ceil(total / pageSize)

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Поиск по имени..."
          value={search}
          onChange={handleSearch}
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
        />
      </div>

      {/* Count */}
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 px-1">
        {total} {total === 1 ? 'человек' : total < 5 ? 'человека' : 'человек'}
      </p>

      {/* List */}
      <div className="flex-1 overflow-y-auto -mx-1">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <div className="w-7 h-7 border-3 border-slate-100 border-t-indigo-500 rounded-full animate-spin border-[3px]" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-10 text-slate-400">
            <UserX size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm font-bold">Нет данных</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {results.map((person, i) => (
              <PersonRow key={`${person.person_type}-${person.person_id || i}`} person={person} />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-100">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ← Назад
          </button>
          <span className="text-[11px] font-bold text-slate-400">{page} / {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Далее →
          </button>
        </div>
      )}
    </div>
  )
}
